import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, ShieldCheck, ArrowDown } from "lucide-react";
import { Seo } from "../../components/public/Seo";
import { TrustBar } from "../../components/public/TrustBar";
import { Reviews } from "../../components/public/Reviews";
import { AnatomyOfRoof } from "../../components/public/AnatomyOfRoof";
import { Odometer } from "../../components/public/Odometer";
import { TiltCard } from "../../components/public/TiltCard";
import { MagneticButton } from "../../components/public/MagneticButton";
import { CtaBand, SectionHeading } from "../../components/public/Shared";
import { BookingCTA } from "../../components/public/BookingCTA";
import { HeroParticles } from "../../components/three/HeroParticles";
import { Anatomy3D } from "../../components/three/Anatomy3D";
import { ServiceMapSection } from "../../components/three/ServiceMap3D";
import { use3DEnabled } from "../../hooks/use3D";
import { SERVICES } from "../../data/site";
import { COMPANY as CO, useSiteContent, api, mediaUrl } from "../../lib/api";
import { BeforeAfterSlider } from "../../components/public/BeforeAfterSlider";

function Hero() {
  const ref = useRef(null);
  const enabled = use3DEnabled();
  const content = useSiteContent();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#1A2421]">
      <motion.div style={{ scale, y }} className="absolute inset-0">
        <BeforeAfterSlider beforeUrl={content.hero_before_url} afterUrl={content.hero_after_url} />
      </motion.div>

      {/* WebGL light/dust motes layered over the video for cinematic depth */}
      {enabled && <HeroParticles />}

      <div className="absolute inset-0 bg-gradient-to-b from-[#0f1814]/70 via-[#224733]/35 to-[#1A2421]/95" />
      <div className="absolute inset-0 wf-grain" />

      <motion.div style={{ opacity: fade }} className="relative h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="inline-flex items-center gap-2 self-start bg-white/10 backdrop-blur border border-white/15 rounded-full px-4 py-2 text-white/90 text-sm font-semibold mb-6">
          <ShieldCheck size={15} className="text-[#62B848]" /> Licensed &amp; insured · Perth, WA · since {CO.since}
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 50, z: -40 }} animate={{ opacity: 1, y: 0, z: 0 }} transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-black heading-up uppercase tracking-tight text-white text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.88] max-w-5xl">
          The Roofing<br /><span className="text-[#62B848]">Specialists</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }}
          className="mt-6 text-white/80 text-lg sm:text-xl max-w-xl leading-relaxed">
          Affordable prices with quality workmanship — residential &amp; commercial metal roofing across {CO.area}. Building roofs since {CO.since}.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.65 }}
          className="mt-9 flex flex-col sm:flex-row gap-3">
          <BookingCTA testId="hero-quote-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#62B848] hover:bg-[#4E9B38] text-white font-bold px-8 py-4 rounded-full transition-colors text-lg cursor-pointer">
            Get a free quote <ArrowRight size={20} />
          </BookingCTA>
          <a href={CO.phoneHref} data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-bold px-8 py-4 rounded-full transition-colors text-lg">
            <Phone size={18} /> {CO.phone}
          </a>
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 flex flex-col items-center gap-1">
        <span className="text-xs uppercase tracking-widest">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}><ArrowDown size={18} /></motion.div>
      </motion.div>
    </section>
  );
}

function ServicesSnapshot() {
  return (
    <section data-testid="services-snapshot" className="py-24 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <SectionHeading eyebrow="What we do" title={<>Roofing done right —<br />residential &amp; commercial.</>} sub="" />
          <Link to="/services" className="hidden md:inline-flex items-center gap-2 text-[#224733] font-bold hover:gap-3 transition-all">
            All services <ArrowRight size={18} />
          </Link>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" style={{ perspective: 1200 }}>
          {SERVICES.map((s, i) => (
            <motion.div key={s.slug} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ delay: (i % 4) * 0.06 }}>
              <TiltCard className="h-full">
                <Link to={`/services/${s.slug}`} data-testid={`svc-card-${s.slug}`}
                  className="group block h-full bg-white rounded-3xl border border-black/5 p-7 shadow-sm hover:shadow-2xl transition-all duration-300">
                  <div className="grid place-items-center w-[52px] h-[52px] rounded-2xl bg-[#224733]/8 text-[#224733] mb-5 group-hover:bg-[#62B848] group-hover:text-white transition-colors" style={{ transform: "translateZ(40px)" }}>
                    <s.icon size={24} />
                  </div>
                  <h3 className="font-display font-bold text-lg text-[#1A2421] mb-2 leading-tight" style={{ transform: "translateZ(25px)" }}>{s.name}</h3>
                  <p className="text-[#4A5550] text-sm leading-relaxed mb-4">{s.tagline}</p>
                  <span className="inline-flex items-center gap-1 text-[#62B848] font-bold text-sm group-hover:gap-2 transition-all">Learn more <ArrowRight size={15} /></span>
                </Link>
              </TiltCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const items = [
    { value: new Date().getFullYear() - CO.since, suffix: "+", label: "Years building roofs" },
    { value: 2400, suffix: "+", label: "Roofs completed" },
    { value: 180000, suffix: "+", label: "m² re-roofed" },
    { value: 100, suffix: "%", label: "Licensed & insured" },
  ];
  return (
    <section data-testid="stats" className="py-20 bg-[#224733] text-white">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {items.map((it, i) => (
          <div key={i} className="text-center">
            <div className="font-display font-black heading-up text-5xl lg:text-6xl text-[#62B848] leading-none">
              {it.custom ? it.custom : <Odometer value={it.value} suffix={it.suffix} />}
            </div>
            <div className="mt-3 text-white/70 font-medium">{it.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AnatomySection() {
  const enabled = use3DEnabled();
  if (!enabled) return <AnatomyOfRoof />;
  return (
    <section data-testid="anatomy-section" className="relative py-24 md:py-36 bg-[#1A2421] text-white overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-8 items-center">
        <div className="max-w-xl">
          <p className="text-[#62B848] font-bold tracking-wide uppercase text-sm mb-3">Anatomy of a roof</p>
          <h2 className="font-display font-extrabold heading-up uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-none mb-4">
            Every layer matters. We get them all right.
          </h2>
          <p className="text-white/60 text-lg leading-relaxed">
            A roof is a system, not a single sheet. Scroll to watch it come apart — sheeting, flashing, insulation, gutters and downpipes — each installed to spec to keep Perth homes and commercial assets watertight.
          </p>
        </div>
        <div className="relative h-[440px] md:h-[560px]">
          <Anatomy3D />
        </div>
      </div>
    </section>
  );
}

function GalleryStrip() {
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);

  const recent = projects.filter((p) => p.status === "completed").slice(0, 3);
  if (recent.length === 0) return null;

  return (
    <section data-testid="recent-work" className="py-24 md:py-32 bg-[#F8F9FA] overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <SectionHeading eyebrow="Recent work" title={<>Real Perth jobs,<br />real results.</>} sub="" />
          <Link to="/our-work" className="hidden md:inline-flex items-center gap-2 text-[#224733] font-bold hover:gap-3 transition-all">
            View all work <ArrowRight size={18} />
          </Link>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {recent.map((p, i) => {
            const cover = p.photos?.[0] || p.after_url || p.before_url;
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }} transition={{ delay: i * 0.08 }}>
                <TiltCard className="h-full" max={6}>
                  <Link to={`/our-work/${p.slug}`} data-testid={`recent-project-${p.slug}`} className="group relative block overflow-hidden rounded-3xl h-[340px]">
                    {cover ? <img src={mediaUrl(cover)} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      : <div className="w-full h-full bg-[#224733]/10" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 p-5">
                      <span className="inline-block text-xs font-bold text-[#0c1a12] bg-[#62B848] px-2.5 py-1 rounded-full mb-2">{p.project_type || "Completed"}</span>
                      <div className="text-white font-display font-bold text-lg leading-tight">{p.title}</div>
                      {p.suburb && <div className="text-white/70 text-sm mt-0.5">{p.suburb}</div>}
                    </div>
                  </Link>
                </TiltCard>
              </motion.div>
            );
          })}
        </div>
        <div className="md:hidden mt-8">
          <Link to="/our-work" className="inline-flex items-center gap-2 text-[#224733] font-bold">View all work <ArrowRight size={18} /></Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo path="/" />
      <Hero />
      <TrustBar />
      <ServicesSnapshot />
      <Stats />
      <AnatomySection />
      <GalleryStrip />
      <ServiceMapSection />
      <Reviews compact />
      <CtaBand />
    </>
  );
}
